import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RiCloseLine, RiCameraLine } from '@remixicon/react';

// In-page QR / barcode scanner via the native BarcodeDetector API — a
// progressive enhancement. Supported on Chromium (Android/desktop); NOT on
// iOS Safari, where the caller hides the entry point and the phone's own
// camera app opens the QR link anyway. No third-party library, no bundle cost.
//
//   getUserMedia(env camera) → <video> → rAF loop → detector.detect(frame)
//        │ first hit ─────────────────────────────► onDetected(rawValue)
//        └ permission denied / no device ─────────► inline error, manual entry
//
// Detected value may be a full public-track URL (…/track/{resi}) or a bare
// resi; parsing is the caller's job. Tracks + rAF are always torn down on close.

// BarcodeDetector isn't in TS's lib yet — declare the slice we use.
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (opts?: {
  formats?: string[];
}) => BarcodeDetectorLike;

export const isBarcodeScanSupported = (): boolean =>
  typeof window !== 'undefined' && 'BarcodeDetector' in window;

interface BarcodeScannerProps {
  onDetected: (rawValue: string) => void;
  onClose: () => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onDetected,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const doneRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    doneRef.current = true;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    stop();
    onClose();
  }, [stop, onClose]);

  useEffect(() => {
    const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor })
      .BarcodeDetector;
    if (!Ctor) {
      setError('Perangkat ini tidak mendukung pemindaian dalam aplikasi.');
      return;
    }
    const detector = new Ctor({
      formats: ['qr_code', 'code_128', 'ean_13', 'code_39'],
    });

    const tick = async () => {
      if (doneRef.current || !videoRef.current) return;
      try {
        const found = await detector.detect(videoRef.current);
        const value = found[0]?.rawValue?.trim();
        if (value) {
          stop();
          onDetected(value);
          return;
        }
      } catch {
        // transient decode error — keep scanning
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        if (doneRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
        rafRef.current = requestAnimationFrame(tick);
      })
      .catch(() => {
        setError(
          'Tidak dapat mengakses kamera. Izinkan akses kamera atau ketik nomor resi.'
        );
      });

    return stop;
    // Run once on mount; callbacks are stable via useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-static-black">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-sm font-medium text-static-white">
          Pindai QR / Barcode
        </span>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Tutup pemindai"
          className="flex size-9 items-center justify-center rounded-full bg-static-white/15 text-static-white"
        >
          <RiCloseLine className="size-5" />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        {error ? (
          <div className="px-8 text-center">
            <RiCameraLine className="mx-auto mb-3 size-8 text-static-white/60" />
            <p className="text-sm text-static-white/80">{error}</p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-5 rounded-lg bg-static-white px-4 py-2 text-sm font-medium text-static-black"
            >
              Ketik Nomor Resi
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              muted
              playsInline
            />
            {/* Reticle */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="size-56 rounded-2xl border-2 border-static-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
            </div>
            <p className="absolute bottom-10 left-0 right-0 px-8 text-center text-sm text-static-white/80">
              Arahkan kamera ke QR pada label
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default BarcodeScanner;
